import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, useMediaQuery } from '@mui/material';
import Dropdown from '../ui/dropdown';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import theme from '../../util/theme/theme';

interface AddUserPopupProps {
    open: boolean;
    onClose: () => void;
    onCreate: (name: string, role: string, email: string) => void;
    roleOptions?: { value: string; label: string }[];
    // When set, the popup pre-fills these values and switches into "edit"
    // labeling (title/button text) instead of the default "add" flow. The
    // email is read-only when editing - pending or accepted - to invite a
    // different email, the invite is deleted and a new one added.
    initialData?: { name: string; role: string; email: string; isPending: boolean } | null;
    // Editing your own account - the role can't be changed (by anyone,
    // admins included; enforced server-side too).
    roleLocked?: boolean;
}

const AddUserPopup: React.FC<AddUserPopupProps> = ({ open, onClose, onCreate, roleOptions = [], initialData = null, roleLocked = false }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isEdit = !!initialData;
    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState<{ name?: string; role?: string; email?: string }>({});

    React.useEffect(() => {
        if (!open) return;
        setName(initialData?.name ?? '');
        setRole(initialData?.role ?? '');
        setEmail(initialData?.email ?? '');
        setErrors({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialData?.name, initialData?.role, initialData?.email]);

    const validate = () => {
        const newErrors: { name?: string; role?: string; email?: string } = {};
        if (!name) newErrors.name = 'Name is required';
        if (!role) newErrors.role = 'Role is required';
        if (!email) {
            newErrors.email = 'Email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                newErrors.email = 'Enter a valid email address.';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreate = () => {
        if (!validate()) return;

        onCreate(name, role, email);
        setName('');
        setRole('');
        setEmail('');
    };

    const handleClose = () => {
        setName('');
        setRole('');
        setEmail('');
        setErrors({});
        onClose();
    };

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>{isEdit ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                    <Typography fontSize={15} color="var(--color-text-muted)">
                        Name <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>
                <Box>
                    <Typography fontSize={15} color="var(--color-text-muted)">
                        Specify Role <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <Dropdown
                        options={roleOptions.length ? roleOptions : [
                            { value: 'admin', label: 'Admin' },
                            { value: 'user', label: 'Read Only' },
                        ]}
                        value={role}
                        onChange={val => setRole(val)}
                        placeholder="Select Role"
                        isPrimary={false}
                        borderRadius={1}
                        error={!!errors.role}
                        helperText={roleLocked ? "You can't change your own role" : errors.role}
                        disabled={roleLocked}
                        height={40}
                    />
                </Box>
                <Box>
                    <Typography fontSize={15} color="var(--color-text-muted)">
                        Email <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        error={!!errors.email}
                        // Never editable once added - to invite a different
                        // email, delete this invite and add a new one.
                        helperText={errors.email || (isEdit ? 'To use a different email, delete this user and add a new one.' : undefined)}
                        disabled={isEdit}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button onClick={handleClose} variant="contained" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                Cancel
                </Button>
                <Button onClick={handleCreate} variant="contained" sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-primary)' } }}>
                {isEdit ? 'Save Changes' : 'Add User'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default AddUserPopup;
